import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import CollapsibleSection from "../../pages/common-features/CollapsibleSection";
import DetailRow from "../../pages/common-features/DetailRow";
import SummarySectionHeader from "../../components/applicantDetailsTabs/SummarySectionHeader";
import {
  splitCoOwnersIntoSections,mapCollateral
} from "../../../src/utils/helpers";
interface CollateralDetailsTabProps {
  collateralDetails: any[];
  isMobile: boolean;
  pageTypeSummary?:string;
  partnerStartedJourny?:boolean;
}

const CollateralDetailsTab: React.FC<CollateralDetailsTabProps> = ({
  collateralDetails,
  isMobile,
  pageTypeSummary,
  partnerStartedJourny,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!collateralDetails || collateralDetails.length === 0)
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );

  const sortFields = (fields: any[], isProperty: boolean) => {
    if (!fields) return [];

    const orderPriority: { [key: string]: number } = {
      "collateral type": 0,
      "per price unit": 1,
      "price per unit": 1,
      "number of units": 2,
      "no of units": 2,
      "collateral value": 3,
      totalValue: 3,
      "property type": 4,
      "property sub type": 5,
      address: 6,
      "address line 1": 6,
      "address line 2": 7,
      city: 8,
      "city name": 8,
      state: 9,
      "state name": 9,
      country: 10,
      "country name": 10,
      pincode: 11,
      "co owner name": 20,
      "owner ship percentage": 21,
      "ownership percentage": 21,
      "phone number": 22,
    };

    const filteredFields = fields.filter((field: any) => {
      const name = field.name || "";
      if (name === "totalValue") {
        return isProperty;
      }

      return (
        name !== "addressType" &&
        name !== "landmark" &&
        name !== "area"
      );
    });
    return filteredFields;
    // return filteredFields.sort((a: any, b: any) => {
    //   const labelA = (
    //     a.name === "totalValue" ? "collateral value" : a.label || ""
    //   ).toLowerCase();
    //   const labelB = (
    //     b.name === "totalValue" ? "collateral value" : b.label || ""
    //   ).toLowerCase();

    //   const rankA =
    //     orderPriority[labelA] !== undefined ? orderPriority[labelA] : 999;
    //   const rankB =
    //     orderPriority[labelB] !== undefined ? orderPriority[labelB] : 999;

    //   return rankA - rankB;
    // });
  };

  const subHeaderStyle = {
    fontSize: "15px",
    fontWeight: 600,
    color: "#6f6b7d",
    mb: 3,
  };
  // "Has Collateral?" is a standalone answer, not part of any one numbered
  // collateral entry — show it once, above every Collateral N collapsible
  // section, instead of nested inside whichever group it happened to be
  // bucketed into.
  const isHasCollateralField = (field: any) =>
    String(field?.name || field?.label || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "") === "hascollateral";
  const hasCollateralEntry = collateralDetails
    .flatMap((item: any) => {
      const key = Object.keys(item || {})[0];
      return item?.[key]?.collateral || [];
    })
    .find(isHasCollateralField);
  // Matches the journey: the collateral section only ever shows when the
  // answer is Yes — if it's No, don't render any Collateral N group at all,
  // even if a blank/leftover field record happens to still exist for one.
  const isHasCollateralNo =
    String(hasCollateralEntry?.value ?? "").trim().toLowerCase() === "no";

  return (
    <Grid item xs={12}>
      {hasCollateralEntry && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <DetailRow
              item={{
                label: "Has Collateral",
                value: hasCollateralEntry.value,
                isVerified: false,
              }}
            />
          </Grid>
        </Grid>
      )}
      {collateralDetails.map((collateralItem: any, index: number) => {
        const collateralKey = Object.keys(collateralItem)[0];
        const collateralNum = collateralKey.replace(/collateral/i, "");
        const block = collateralItem[collateralKey] || {};
        const collateral = (block.collateral || []).filter(
          (field: any) => !isHasCollateralField(field),
        );
        const ownership = block.ownership || [];
        let ownerShipDetails=splitCoOwnersIntoSections(ownership);
        const isProperty =
          Array.isArray(collateral) &&
          collateral.some((field: any) => {
            const label = (field.label || "").toLowerCase();
            const name = (field.name || "").toLowerCase();
            const value = (field.value || "").toString().toLowerCase();

            return (
              (name === "collateraltype" || label === "collateral type") &&
              value.includes("property")
            );
          });
        const sortedCollateralFields = sortFields(collateral, isProperty);
        const isExpanded = expandedIndex === index;
        // "Has Collateral?" = No — no Collateral N group should show at
        // all, even if a blank/leftover field record still exists for one.
        if (isHasCollateralNo) {
          return null;
        }
        // Nothing left to show for this group once "Has Collateral?" is
        // pulled out above and blank fields are ignored (e.g. field keys
        // that exist but were never actually filled in) — skip the
        // otherwise-empty collapsible section entirely.
        const hasAnyFilledCollateralField = sortedCollateralFields.some(
          (field: any) => field?.value !== "" && field?.value !== null && field?.value !== undefined,
        );
        if (!hasAnyFilledCollateralField && (!ownerShipDetails || ownerShipDetails.length === 0)) {
          return null;
        }
        return (
          <Box key={collateralKey} sx={{ mb: index < collateralDetails.length - 1 ? 1 : 0 }}>
            <CollapsibleSection
              title={`Collateral ${index + 1}`}
              isExpanded={isExpanded || pageTypeSummary==="loansummary"}
              onToggle={() =>
                setExpandedIndex(isExpanded ? null : index)
              }
            >
              {hasAnyFilledCollateralField && (
                <Box sx={{ mb: ownership?.length ? 4 : 0 }}>
                  {pageTypeSummary!=="loansummary" &&
                    partnerStartedJourny===false && 
                  <Typography sx={{ ...subHeaderStyle }}>
                    Collateral Information
                  </Typography>
                  }
                  {pageTypeSummary!=="loansummary" &&
                    partnerStartedJourny===true && 
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Collateral Information"}
                        />
                      </Box>
                    </Grid>
                  }
                  {
                    pageTypeSummary==="loansummary" && 
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Collateral Information"}
                        />
                      </Box>
                    </Grid>
                  }
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      ml: isMobile ? 0.2 : -2,
                      width: isMobile ? "auto" : "calc(100% + 16px)",
                    }}
                  >
                    {sortedCollateralFields.map((field: any, fieldIndex: number) => {
                      const labelOverrides:any = {
                        "Fd Number": "FD Number",
                        "Fd Holder Name": "FD Holder Name",
                      };

                      const updatedLabel = labelOverrides[field.label] ?? field.label;
                      const displayLabel =
                        field.name === "totalValue"
                          ? "Collateral Value"
                          : updatedLabel;
                        const displayValue =field.value;
                      return (
                        field.value!="" && 
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          key={`${collateralKey}-field-${fieldIndex}`}
                        >
                          <DetailRow
                            item={{
                              label: displayLabel,
                              value: displayValue,
                              isVerified: false,
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {ownerShipDetails && ownerShipDetails.length > 0 && (
                
                <Box>
                {
                  pageTypeSummary!=="loansummary" && 
                   partnerStartedJourny===false && 
                  <Typography sx={{ ...subHeaderStyle }}>
                    Co-Owner Information
                  </Typography>
                }
                {pageTypeSummary!=="loansummary" &&
                    partnerStartedJourny===true && 
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Co-Owner Information"}
                        />
                      </Box>
                    </Grid>
                  }
                  {
                    pageTypeSummary==="loansummary" && 
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, mt: 2 }}>
                        <SummarySectionHeader
                          title={"Co-Owner Information"}
                        />
                      </Box>
                    </Grid>
                  }
                  {ownerShipDetails.map((coOwner) => {
                    return (
                      <Grid
                        container
                        spacing={2}
                        key={`coOwner-card-${coOwner.id}`}
                        sx={{
                          ml: isMobile ? 0.2 : -2,
                          width: isMobile ? "auto" : "calc(100% + 16px)",
                          mb: 2,
                        }}
                      >
                        {/* 1. Loop through the cleaned fields array for this specific person */}
                        {coOwner.fields.map((field:any, fieldIndex:number) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            key={`${coOwner.id}-field-${fieldIndex}`}
                          >
                            <DetailRow
                              item={{
                                label: field.label,
                                value: field.value,
                                isVerified: false,
                              }}
                            />
                          </Grid>
                        ))}

                        {/* 2. Optional spacer column layout element matching your legacy template */}
                        <Grid
                          item
                          md={3}
                          sx={{ display: { xs: "none", md: "block" } }}
                          aria-hidden="true"
                        />
                      </Grid>
                    );
                  })}
                </Box>
              )}
            </CollapsibleSection>
          </Box>
        );
      })}
    </Grid>
  );
};

export default CollateralDetailsTab;